import { useState, useMemo, useContext } from "react";
import { useQuery } from "@tanstack/react-query";
import { Eye, Search, ChevronDown, Filter, X } from "lucide-react";
import { Button } from "./button";
import { Badge } from "./badge";
import { Input } from "./input";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./select";
import { Skeleton } from "./skeleton";
import {
  fetchUserRoles,
  fetchRoles,
  impersonateUser,
  type UserRoleAssignment,
  type AdminRole,
} from "@/lib/api/rbac";
import { RBACContext } from "@/lib/rbac/context";
import { SUPER_ADMIN_ROLE } from "@/lib/rbac/types";

export default function ViewAsDropdown() {
  const { roles: myRoles } = useContext(RBACContext);
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedRoleFilter, setSelectedRoleFilter] = useState<number | null>(null);
  const [selectedScopeFilter, setSelectedScopeFilter] = useState<string>("");
  const [isImpersonating, setIsImpersonating] = useState(false);

  // Check if already impersonating
  const isCurrentlyImpersonating = !!localStorage.getItem(
    "impersonation_original_access_token"
  );

  // Current user's primary role display
  const currentRoleLabel = useMemo(() => {
    if (isCurrentlyImpersonating) {
      try {
        const user = JSON.parse(localStorage.getItem("user") || "{}");
        return user.roles?.[0] || "Impersonated";
      } catch {
        return "Impersonated";
      }
    }
    if (myRoles.length === 0) return "No Role";
    const primary = myRoles[0];
    return primary.role_display_name || primary.role_name;
  }, [myRoles, isCurrentlyImpersonating]);

  // Fetch all roles for filter dropdown
  const { data: allRoles } = useQuery({
    queryKey: ["rbac-roles-list"],
    queryFn: fetchRoles,
    enabled: isOpen,
    staleTime: 5 * 60 * 1000,
  });

  // Fetch user role assignments
  const { data: assignmentsData, isLoading: assignmentsLoading } = useQuery({
    queryKey: [
      "rbac-user-roles-for-impersonation",
      { role_id: selectedRoleFilter, search, is_active: true },
    ],
    queryFn: () =>
      fetchUserRoles({
        role_id: selectedRoleFilter ?? undefined,
        is_active: true,
        search: search || undefined,
      }),
    enabled: isOpen,
    staleTime: 60 * 1000,
  });

  const assignments = assignmentsData?.results ?? [];

  // Filter by scope type
  const filteredAssignments = useMemo(() => {
    let result = assignments;
    if (selectedScopeFilter) {
      result = result.filter((a) => a.scope_type === selectedScopeFilter);
    }
    return result;
  }, [assignments, selectedScopeFilter]);

  // Get unique scope types from assignments for filter
  const scopeTypes = useMemo(() => {
    const types = new Set(assignments.map((a) => a.scope_type));
    return Array.from(types).sort();
  }, [assignments]);

  const handleImpersonate = async (assignment: UserRoleAssignment) => {
    setIsImpersonating(true);
    try {
      // Store original tokens before impersonating
      const origAccess = localStorage.getItem("access_token");
      const origRefresh = localStorage.getItem("refresh_token");
      const origUser = localStorage.getItem("user");

      if (origAccess && !localStorage.getItem("impersonation_original_access_token")) {
        localStorage.setItem("impersonation_original_access_token", origAccess);
        if (origRefresh) localStorage.setItem("impersonation_original_refresh_token", origRefresh);
        if (origUser) localStorage.setItem("impersonation_original_user", origUser);
      }

      const result = await impersonateUser(assignment.user);

      // Set new tokens
      localStorage.setItem("access_token", result.access_token);
      localStorage.setItem("refresh_token", result.refresh_token);
      localStorage.setItem("user", JSON.stringify(result.user));

      // Reload to apply new permissions
      setIsOpen(false);
      window.location.reload();
    } catch (error) {
      console.error("Impersonation failed:", error);
    } finally {
      setIsImpersonating(false);
    }
  };

  // Only super_admin can impersonate
  const canImpersonate = myRoles.some(
    (r) => r.role_name === SUPER_ADMIN_ROLE
  );

  if (!canImpersonate && !isCurrentlyImpersonating) return null;

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
      <Button
        variant="outline"
        size="sm"
        className="flex items-center gap-1.5 h-8 text-xs font-medium"
        aria-label={`View as: ${currentRoleLabel}`}
      >
        <Eye className="h-3.5 w-3.5" />
        <span className="hidden sm:inline">View As:</span>
        <span className="font-semibold max-w-[120px] truncate">
          {currentRoleLabel}
        </span>
        <ChevronDown className="h-3 w-3" />
      </Button>
      </PopoverTrigger>

          <PopoverContent
            align="end"
            className="w-[min(420px,calc(100vw-2rem))] max-h-[500px] p-0 flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div className="px-4 py-3 border-b bg-muted/40">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold">Switch Role View</h3>
                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  aria-label="Close"
                  className="text-muted-foreground hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <p className="text-xs text-muted-foreground">
                Select a user role to impersonate their permissions and view.
              </p>
            </div>

            {/* Filters */}
            <div className="px-4 py-2 border-b space-y-2">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                <Input
                  placeholder="Search by username..."
                  aria-label="Search by username"
                  className="pl-8 h-8 text-xs"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>

              {/* Role + Scope filters */}
              <div className="flex gap-2">
                <Select
                  value={selectedRoleFilter != null ? String(selectedRoleFilter) : "all"}
                  onValueChange={(v) => setSelectedRoleFilter(v === "all" ? null : Number(v))}
                >
                  <SelectTrigger className="h-7 flex-1 text-xs" aria-label="Filter by role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Roles</SelectItem>
                    {allRoles?.map((role: AdminRole) => (
                      <SelectItem key={role.id} value={String(role.id)}>
                        {role.display_name || role.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select
                  value={selectedScopeFilter || "all"}
                  onValueChange={(v) => setSelectedScopeFilter(v === "all" ? "" : v)}
                >
                  <SelectTrigger className="h-7 flex-1 text-xs" aria-label="Filter by scope">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Scopes</SelectItem>
                    {scopeTypes.map((scope) => (
                      <SelectItem key={scope} value={scope}>
                        {scope.charAt(0).toUpperCase() + scope.slice(1)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {(selectedRoleFilter || selectedScopeFilter || search) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-xs"
                    onClick={() => {
                      setSearch("");
                      setSelectedRoleFilter(null);
                      setSelectedScopeFilter("");
                    }}
                  >
                    <Filter className="h-3 w-3 mr-1" />
                    Clear
                  </Button>
                )}
              </div>
            </div>

            {/* Assignments list */}
            <div className="flex-1 overflow-y-auto max-h-[320px]">
              {assignmentsLoading ? (
                <div className="space-y-3 p-4" aria-busy="true" aria-label="Loading">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} className="space-y-1.5">
                      <Skeleton className="h-4 w-40" />
                      <Skeleton className="h-3 w-56" />
                    </div>
                  ))}
                </div>
              ) : filteredAssignments.length === 0 ? (
                <div className="text-center py-8 text-sm text-muted-foreground">
                  No role assignments found.
                </div>
              ) : (
                <div className="divide-y">
                  {filteredAssignments.map((assignment) => (
                    <button
                      type="button"
                      key={assignment.id}
                      className="w-full px-4 py-2.5 flex items-center gap-3 hover:bg-muted/50 transition-colors text-left disabled:opacity-50"
                      onClick={() => handleImpersonate(assignment)}
                      disabled={isImpersonating}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium truncate">
                            {assignment.role_display_name}
                          </span>
                          <Badge
                            variant="secondary"
                            className="text-[10px] shrink-0"
                          >
                            {assignment.scope_type}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <span className="text-xs text-muted-foreground truncate">
                            {assignment.scope_label || "Global"}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            &middot;
                          </span>
                          <span className="text-xs text-muted-foreground truncate">
                            @{assignment.username}
                          </span>
                        </div>
                      </div>
                      <Eye className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </PopoverContent>
    </Popover>
  );
}
